// ── NSV Loyalty Tier System ──────────────────────────────────
// Pure calculation logic for client volume discount tiers
// No DB schema changes required — computed on-the-fly from jobs
//
// Tiers (rolling 30-day window):
//   Bronze:   0-9 orders  (standard pricing)
//   Silver:   10-24 orders (5% discount)
//   Gold:     25-49 orders (10% discount)
//   Platinum: 50+ orders   (15% discount + dedicated manager)
//
// Grace period: 15 days. If client was at a higher tier in the
// previous 30-day window, they keep that tier for 15 days even
// if their current rolling 30-day count drops.

export const TIERS = [
  {
    key:        'bronze',
    label:      'Bronze',
    icon:       '◆',
    min:        0,
    max:        9,
    discount:   0,
    color:      '#41427a',
    accentColor:'#4D4E8D',
    bgGradient: 'linear-gradient(135deg,#4D4E8D,#41427a)',
    perks:      'Standard pricing — your journey begins here.',
  },
  {
    key:        'silver',
    label:      'Silver',
    icon:       '◆',
    min:        10,
    max:        24,
    discount:   5,
    color:      '#41427a',
    accentColor:'#4D4E8D',
    bgGradient: 'linear-gradient(135deg,#4D4E8D,#41427a)',
    perks:      '5% volume discount applied to every signing.',
  },
  {
    key:        'gold',
    label:      'Gold',
    icon:       '◆',
    min:        25,
    max:        49,
    discount:   10,
    color:      '#41427a',
    accentColor:'#4D4E8D',
    bgGradient: 'linear-gradient(135deg,#4D4E8D,#41427a)',
    perks:      '10% volume discount applied to every signing.',
  },
  {
    key:        'platinum',
    label:      'Platinum',
    icon:       '◆',
    min:        50,
    max:        Infinity,
    discount:   15,
    color:      '#41427a',
    accentColor:'#4D4E8D',
    bgGradient: 'linear-gradient(135deg,#4D4E8D,#41427a)',
    perks:      '15% discount + dedicated account manager.',
  },
]

export const GRACE_PERIOD_DAYS = 15

// ── Determine tier from order count ───────────────────────────
export function getTierByCount(count) {
  return TIERS.find(t => count >= t.min && count <= t.max) || TIERS[0]
}

// ── Count jobs in a date window ───────────────────────────────
// Counts orders that progressed past 'new' status in the window
// (we count assigned/confirmed/completed/paid as "real" orders)
export function countJobsInWindow(jobs, daysAgoStart, daysAgoEnd = 0) {
  if (!Array.isArray(jobs)) return 0
  const now      = Date.now()
  const msPerDay = 24 * 60 * 60 * 1000
  const start    = now - (daysAgoStart * msPerDay)
  const end      = now - (daysAgoEnd   * msPerDay)
  const COUNTABLE_STATUSES = ['assigned','confirmed','completed','paid']

  return jobs.filter(j => {
    if (!j.created_at) return false
    if (j.status === 'cancelled') return false
    if (!COUNTABLE_STATUSES.includes(j.status) && j.status !== 'pending_acceptance' && j.status !== 'new') return false
    const t = new Date(j.created_at).getTime()
    return t >= start && t <= end
  }).length
}

// ── Main tier calculation with grace period ───────────────────
// Returns { current, effective, isGrace, graceDaysRemaining,
//          currentCount, nextTier, ordersToNext, progressPct }
export function calculateTier(jobs) {
  // Current rolling 30-day window
  const currentCount = countJobsInWindow(jobs, 30, 0)
  const currentTier  = getTierByCount(currentCount)

  // Previous rolling 30-day window (30-60 days ago)
  const previousCount = countJobsInWindow(jobs, 60, 30)
  const previousTier  = getTierByCount(previousCount)

  // Grace logic: if previous tier was HIGHER, give grace
  let effectiveTier       = currentTier
  let isGrace             = false
  let graceDaysRemaining  = 0

  const previousIsHigher = TIERS.indexOf(previousTier) > TIERS.indexOf(currentTier)

  if (previousIsHigher) {
    // Find the most recent job that established the previous tier
    // (simplified: use the most recent job in the previous window as the anchor)
    const msPerDay = 24 * 60 * 60 * 1000
    const previousWindowJobs = (jobs || [])
      .filter(j => {
        if (!j.created_at) return false
        const ageDays = (Date.now() - new Date(j.created_at).getTime()) / msPerDay
        return ageDays >= 30 && ageDays <= 60
      })
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

    if (previousWindowJobs.length > 0) {
      const anchorDate    = new Date(previousWindowJobs[0].created_at)
      const graceEnd      = new Date(anchorDate.getTime() + (30 + GRACE_PERIOD_DAYS) * msPerDay)
      const daysRemaining = Math.ceil((graceEnd.getTime() - Date.now()) / msPerDay)

      if (daysRemaining > 0) {
        effectiveTier      = previousTier
        isGrace            = true
        graceDaysRemaining = daysRemaining
      }
    }
  }

  // Calculate progress to next tier
  const effectiveIdx = TIERS.indexOf(effectiveTier)
  const nextTier     = effectiveIdx < TIERS.length - 1 ? TIERS[effectiveIdx + 1] : null

  let ordersToNext = 0
  let progressPct  = 100

  if (nextTier) {
    ordersToNext = Math.max(0, nextTier.min - currentCount)
    // Progress within the current tier
    const tierStart = effectiveTier.min
    const tierEnd   = nextTier.min
    progressPct     = Math.min(100, Math.max(0, Math.round(
      ((currentCount - tierStart) / (tierEnd - tierStart)) * 100
    )))
  }

  return {
    current:            currentTier,
    effective:          effectiveTier,
    isGrace,
    graceDaysRemaining,
    currentCount,
    previousCount,
    nextTier,
    ordersToNext,
    progressPct,
  }
}
